import { useState } from 'react';
import { Link } from 'react-router-dom';
import { newsArticles } from '@/mocks/news';
import FooterSection from '@/pages/home/components/FooterSection';

const categories = ['All', 'Game Recap', 'Roster Move', 'Draft', 'Opinion', 'Injury Report', 'Community'];

export default function NewsPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = newsArticles.filter((a) => {
    const matchesCat = activeCategory === 'All' || a.category === activeCategory;
    const matchesSearch =
      a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.author.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCat && matchesSearch;
  });

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <main className="w-full">
      {/* Page Hero */}
      <section className="relative w-full h-56 md:h-72 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/8d105ec8c04aeb3d4f06acd6b6969875.jpg"
          alt="Vikings News"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#2a0a3d]/90"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 pt-16">
          <p className="text-[#ffd700] text-xs font-bold tracking-widest uppercase mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
            PurplePride.org
          </p>
          <h1 className="text-4xl md:text-5xl font-black text-white uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Vikings News
          </h1>
        </div>
      </section>

      {/* Controls */}
      <section className="w-full bg-white border-b border-gray-100 px-6 md:px-12 py-5 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center gap-4 justify-between">
          {/* Category Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase cursor-pointer transition-all duration-300 ${
                  activeCategory === cat
                    ? 'bg-[#4a1a6b] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-[#4a1a6b]/10 hover:text-[#4a1a6b]'
                }`}
                style={{ fontFamily: "'Oswald', sans-serif" }}
              >
                {cat}
              </button>
            ))}
          </div>
          {/* Search */}
          <div className="relative w-full md:w-56">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder="Search news..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 rounded-full border border-gray-200 bg-gray-50 text-sm text-gray-700 focus:outline-none focus:border-[#4a1a6b] transition-colors"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            />
          </div>
        </div>
      </section>

      <section className="w-full px-6 md:px-12 py-12 bg-[#f8f6fb]">
        <div className="max-w-7xl mx-auto">
          {filtered.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-[#4a1a6b]/10">
                <i className="ri-newspaper-line text-2xl text-[#4a1a6b]"></i>
              </div>
              <p className="text-gray-500 text-sm" style={{ fontFamily: "'Oswald', sans-serif" }}>No articles found.</p>
            </div>
          ) : (
            <>
              {/* Featured Article */}
              {featured && (
                <div className="mb-10 rounded-lg overflow-hidden bg-white border border-gray-100 flex flex-col md:flex-row group cursor-pointer">
                  <div className="relative w-full md:w-96 h-56 md:h-auto flex-shrink-0 overflow-hidden">
                    <img
                      src={featured.image}
                      alt={featured.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#ffd700] text-[#4a1a6b] text-xs font-black tracking-widest uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      {featured.category}
                    </span>
                  </div>
                  <div className="flex-1 p-6 md:p-8 flex flex-col justify-center">
                    <span className="text-[#4a1a6b] text-xs font-bold tracking-widest uppercase mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      Featured Story
                    </span>
                    <h2 className="text-2xl md:text-3xl font-black text-[#2a0a3d] mb-3 leading-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      {featured.title}
                    </h2>
                    <p className="text-gray-500 text-sm leading-relaxed mb-5">{featured.excerpt}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-400" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      <span className="flex items-center gap-1"><i className="ri-user-line"></i> {featured.author}</span>
                      <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {featured.date}</span>
                      <span className="flex items-center gap-1"><i className="ri-time-line"></i> {featured.readTime}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Article Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((article) => (
                  <article key={article.id} className="bg-white rounded-lg overflow-hidden border border-gray-100 group cursor-pointer hover:border-[#4a1a6b]/20 transition-all duration-300">
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-[#4a1a6b] text-white text-[10px] font-black tracking-widest uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
                        {article.category}
                      </span>
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-black text-[#2a0a3d] mb-2 leading-tight line-clamp-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
                        {article.title}
                      </h3>
                      <p className="text-gray-500 text-xs leading-relaxed mb-3 line-clamp-2">{article.excerpt}</p>
                      <div className="flex items-center justify-between text-[10px] text-gray-400 pt-3 border-t border-gray-100" style={{ fontFamily: "'Oswald', sans-serif" }}>
                        <span>{article.author}</span>
                        <span>{article.date}</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <FooterSection />
    </main>
  );
}
